import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Statistic, Spin, message, Divider, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { fetchSalesReport } from '../../core/api/data';

const { Title } = Typography;

const SalesReport = ({ timezone, filters, refreshTrigger }) => {
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState({
    total: {
      orders: 0,
      sales: 0
    },
    sublytics: {
      orders: 0,
      sales: 0
    },
    wordpress: {
      orders: 0,
      sales: 0
    },
    tigertech: {
      orders: 0,
      sales: 0
    },
    socrate: {
      orders: 0,
      sales: 0
    },
    authorize_net: {
      transactions: 0,
      successful: 0,
      failed: 0,
      total_amount: 0
    },
    stripe: {
      transactions: 0,
      successful: 0,
      failed: 0,
      total_amount: 0
    }
  });

  // Function to fetch sales data from API
  const fetchSalesData = async () => {
    setLoading(true);
    
    try {
      // Only fetch if we have filters
      if (filters) {
        console.log('Fetching sales report with filters:', filters);
        const response = await fetchSalesReport(filters);
        console.log('Sales report response:', response);
        setSalesData(response);
      }
    } catch (error) {
      console.error('Error fetching sales report:', error);
      
      // Show more detailed error message if available
      if (error.response && error.response.data) {
        message.error(`Failed to load sales report: ${error.response.data.error || 'Unknown error'}`);
      } else {
        message.error('Failed to load sales report data');
      }
      
      // Set default data in case of error
      setSalesData({
        total: { orders: 0, sales: 0 },
        sublytics: { orders: 0, sales: 0 },
        wordpress: { orders: 0, sales: 0 },
        tigertech: { orders: 0, sales: 0 },
        socrate: { orders: 0, sales: 0 },
        authorize_net: { transactions: 0, successful: 0, failed: 0, total_amount: 0 },
        stripe: { transactions: 0, successful: 0, failed: 0, total_amount: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  // Only fetch data when refresh is triggered (Apply Filters button is clicked)
  // or on initial component mount
  useEffect(() => {
    fetchSalesData();
  }, [refreshTrigger]);

  // Data for the sales summary table
  const columns = [
    {
      title: '',
      dataIndex: 'source',
      key: 'source',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (value, record) => record.source === 'Orders' ? 
        value.toLocaleString() : 
        formatCurrency(value)
    },
    {
      title: 'Sublytics',
      dataIndex: 'sublytics',
      key: 'sublytics',
      render: (value, record) => record.source === 'Orders' ? 
        value.toLocaleString() : 
        formatCurrency(value)
    },
    {
      title: 'WordPress',
      dataIndex: 'wordpress',
      key: 'wordpress',
      render: (value, record) => record.source === 'Orders' ? 
        value.toLocaleString() : 
        formatCurrency(value)
    }
  ];

  const formatCurrency = (value) => {
    // Handle null, undefined, or NaN values
    if (value === null || value === undefined || isNaN(value)) {
      return '$0.00';
    }
    return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const dataSource = [
    {
      key: '1',
      source: 'Orders',
      total: salesData.total.orders,
      sublytics: salesData.sublytics.orders,
      wordpress: salesData.wordpress.orders
    },
    {
      key: '2',
      source: 'Sales ($)',
      total: salesData.total.sales,
      sublytics: salesData.sublytics.sales,
      wordpress: salesData.wordpress.sales
    }
  ];

  // Define columns for TigerTech and Socrate tables - matching the structure of WordPress & Sublytics
  const externalOrdersColumns = [
    {
      title: '',
      dataIndex: 'source',
      key: 'source',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (value, record) => record.source === 'Orders' ? 
        value.toLocaleString() : 
        formatCurrency(value)
    },
    {
      title: 'TigerTech',
      dataIndex: 'tigertech',
      key: 'tigertech',
      render: (value, record) => record.source === 'Orders' ? 
        value.toLocaleString() : 
        formatCurrency(value)
    },
    {
      title: 'Socrate',
      dataIndex: 'socrate',
      key: 'socrate',
      render: (value, record) => record.source === 'Orders' ? 
        value.toLocaleString() : 
        formatCurrency(value)
    }
  ];

  // Data source for TigerTech and Socrate table
  const externalOrdersDataSource = [
    {
      key: '1',
      source: 'Orders',
      total: salesData.tigertech.orders + salesData.socrate.orders,
      tigertech: salesData.tigertech.orders,
      socrate: salesData.socrate.orders
    },
    {
      key: '2',
      source: 'Sales ($)',
      total: salesData.tigertech.sales + salesData.socrate.sales,
      tigertech: salesData.tigertech.sales,
      socrate: salesData.socrate.sales
    }
  ];

  return (
    <div className="sales-report-container">
      <Spin spinning={loading}>
        {/* Main Sales Summary - WordPress and Sublytics */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Sales Summary - WordPress & Sublytics" bordered={false}>
              <Table 
                dataSource={dataSource} 
                columns={columns} 
                pagination={false}
                size="middle"
              />
            </Card>
          </Col>
        </Row>

        {/* TigerTech and Socrate Orders Summary Table */}
        <Row gutter={[16, 16]} className="mt-4">
          <Col span={24}>
            <Card title="Sales Summary - TigerTech & Socrate" bordered={false}>
              <Table 
                dataSource={externalOrdersDataSource} 
                columns={externalOrdersColumns} 
                pagination={false}
                size="middle"
              />
            </Card>
          </Col>
        </Row>

        {/* Transaction Summary Section */}
        <Row gutter={[16, 16]} className="mt-4">
          <Col span={24}>
            <Title level={4}>Transactions Summary</Title>
          </Col>
        </Row>

        {/* Authorize.net Transaction Summary */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Authorize.net Transactions" bordered={false}>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Statistic
                    title="Total Transactions"
                    value={salesData.authorize_net.transactions}
                    precision={0}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Successful"
                    value={salesData.authorize_net.successful}
                    precision={0}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Failed"
                    value={salesData.authorize_net.failed}
                    precision={0}
                    valueStyle={{ color: '#cf1322' }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Total Amount"
                    value={salesData.authorize_net.total_amount}
                    precision={2}
                    valueStyle={{ color: '#1890ff' }}
                    prefix={<DollarOutlined />}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Stripe Transaction Summary */}
        <Row gutter={[16, 16]} className="mt-4">
          <Col span={24}>
            <Card title="Stripe Transactions" bordered={false}>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Statistic
                    title="Total Transactions"
                    value={salesData.stripe.transactions}
                    precision={0}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Available"
                    value={salesData.stripe.successful}
                    precision={0}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Pending"
                    value={salesData.stripe.failed}
                    precision={0}
                    valueStyle={{ color: '#faad14' }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Total Amount"
                    value={salesData.stripe.total_amount}
                    precision={2}
                    valueStyle={{ color: '#1890ff' }}
                    prefix={<DollarOutlined />}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Divider />
      </Spin>
    </div>
  );
};

export default SalesReport;
